import React, { useState, useEffect } from 'react';
import Icon from '@components/Icon/Icon';
import { faDownload, faCheck, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { showInstallPrompt, getInstallability, isStandalone } from '../../utils/pwa';
import './PWAInstallButton.scss';

const PWAInstallButton = ({ className = '', text = 'Install App' }) => {
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showDebug, setShowDebug] = useState(false);

  useEffect(() => {
    // Check if app is already installed
    const installed = isStandalone();
    setIsInstalled(installed);

    // Update installability status
    const updateInstallability = () => {
      const { isInstallable: installable, canPrompt } = getInstallability();
      setIsInstallable(installable);
    };

    // Listen for PWA events
    const handleInstallable = (event) => {
      updateInstallability();
    };

    const handleInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
    };

    window.addEventListener('pwa-installable', handleInstallable);
    window.addEventListener('pwa-installed', handleInstalled);

    // Initial check
    updateInstallability();

    // Show button for debugging if in development
    if (process.env.NODE_ENV === 'development') {
      setShowDebug(true);
    }

    return () => {
      window.removeEventListener('pwa-installable', handleInstallable);
      window.removeEventListener('pwa-installed', handleInstalled);
    };
  }, []);

  const handleInstallClick = () => {
    showInstallPrompt();
  };

  const handleDebugClick = () => {
    const { isInstallable: installable, canPrompt } = getInstallability();
    alert(`PWA Debug:
- Is Installable: ${installable}
- Can Prompt: ${canPrompt}
- Is Installed: ${isInstalled}
- Is Standalone: ${isStandalone()}
- User Agent: ${navigator.userAgent.slice(0, 50)}...
- Secure Context: ${window.isSecureContext}
- Service Worker: ${'serviceWorker' in navigator}
    `);
  };

  // Always show in development or if installable/installed
  const shouldShow = showDebug || isInstallable || isInstalled;

  if (!shouldShow) {
    return null;
  }

  return (
    <div className="pwa-install-container">
      <button
        className={`pwa-install-button ${className} ${isInstalled ? 'installed' : ''}`}
        onClick={handleInstallClick}
        aria-label="Install Sloot App"
        disabled={isInstalled}
      >
        <Icon
          icon={isInstalled ? faCheck : faDownload}
          className="install-icon"
        />
        <span className="install-text">
          {isInstalled ? 'App Installed' : text}
        </span>
      </button>

      {showDebug && (
        <button
          className="pwa-debug-button"
          onClick={handleDebugClick}
          title="Debug PWA Info"
        >
          <Icon icon={faInfoCircle} />
        </button>
      )}
    </div>
  );
};

export default PWAInstallButton;
